import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ethers } from 'ethers';
import { Link } from 'react-router-dom';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers, deduplicateRequest } from './EthersSetup';
import DICY from '../deployedContractsInfo/DICY.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import useBlockchainEvents from '../hooks/useBlockchainEvents';
import { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import { getAddressLink, hasExplorerSupport } from '../utils/BlockchainExplorer.js';
import { FullGameSkeleton, TransactionPanelSkeleton } from './ui/SkeletonLoader';
import TopRightBar from './basics/TopRightBar';

const DICYGame = () => {
  const [bankState, setBankState] = useState({
    totalValue: 0,
    maxBet: 0,
    totalShares: 0
  });

  const [userState, setUserState] = useState({ 
    userAddress: '',
    userShares: 0,
    userValue: 0,
    isInvestor: false
  });

  const [recentBets, setRecentBets] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [uiReady, setUiReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState({
    bankState: true,      // Bank state and basic contract info
    userState: true,      // User balance, shares, investment status
    recentBets: true,     // Recent bet history
    transactions: true,   // Transaction history
    userFeatures: true    // Wallet-dependent features (betting, investing)
  });
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [investorsCollapsed, setInvestorsCollapsed] = useState(true);
  const [betsCollapsed, setBetsCollapsed] = useState(false);
  const [transactionsCollapsed, setTransactionsCollapsed] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [isComponentInitialized, setIsComponentInitialized] = useState(false);

  // Transaction tracking state
  const [transactions, setTransactions] = useState({
    recentBetTxs: [], // Array of recent bet transaction hashes
    recentInvestTxs: [], // Array of recent investment transaction hashes
    recentWithdrawTxs: [], // Array of recent withdrawal transaction hashes
    recentTransferTxs: [] // Array of recent transfer transaction hashes
  });

  // Form states
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawShares, setWithdrawShares] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [number, setNumber] = useState(50); // Player's chosen number (1-100)
  const [pendingTx, setPendingTx] = useState(false);

  // Contract state management
  const [dicyContract, setDicyContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);

  // Contract state management for async creation
  const [readOnlyContract, setReadOnlyContract] = useState(null);
  const [signerContract, setSignerContract] = useState(null);

  // Initialize read-only contract asynchronously
  useEffect(() => {
    const initReadOnlyContract = async () => {
      try {
        const contract = await createContract(contractAddresses.DICY, DICY.abi, false);
        setReadOnlyContract(contract);
      } catch (error) {
        console.warn('[DICY] Failed to create read-only contract:', error);
        setReadOnlyContract(null);
      }
    };
    initReadOnlyContract();
  }, []);

  // Initialize signer contract asynchronously when wallet state changes
  useEffect(() => {
    const initSignerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.DICY, DICY.abi, true);
        setSignerContract(contract);
      } catch (error) {
        console.warn('[DICY] Failed to create signer contract:', error);
        setSignerContract(null);
      }
    };
    if (isWalletInitialized && userState.userAddress) {
      initSignerContract();
    } else {
      setSignerContract(null);
    }
  }, [isWalletInitialized, userState.userAddress]);

  // Bet status and result tracking
  const [betStatus, setBetStatus] = useState('idle'); // 'idle' | 'pending' | 'won' | 'lost'
  const [lastBetResult, setLastBetResult] = useState(null); // { outcome, betAmount, chosenNumber, winAmount }

  // Investment status and result tracking
  const [investStatus, setInvestStatus] = useState('idle'); // 'idle' | 'pending' | 'success'
  const [lastInvestResult, setLastInvestResult] = useState(null); // { amount, newShares, totalBankAfter }

  // Withdrawal status and result tracking
  const [withdrawStatus, setWithdrawStatus] = useState('idle'); // 'idle' | 'pending' | 'success'
  const [lastWithdrawResult, setLastWithdrawResult] = useState(null); // { amount, sharesRedeemed, totalBankAfter }

  // Contract explorer link
  const [contractExplorerLink, setContractExplorerLink] = useState(null);
  
  // Token transfer states
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transfersCollapsed, setTransfersCollapsed] = useState(true);

  // Pause system states
  const [deployerAddress, setDeployerAddress] = useState('');
  const [isPaused, setIsPaused] = useState(false);


  // Staggered loading functions for non-blocking RPC
  const loadBankBasics = useCallback(async () => {
    if (!readOnlyContract) return;
    try {
      console.log('[DICY] Loading bank basics...');

      // Get basic bank information using read-only contract
      const [bankValue, totalSupply, deployer, paused] = await Promise.all([
        readOnlyContract.getBankValue(),
        readOnlyContract.totalSupply(),
        readOnlyContract.deployer(),
        readOnlyContract.paused()
      ]);

      setBankState({
        totalValue: bankValue,
        maxBet: 0, // Will be calculated client-side when needed
        totalShares: totalSupply
      });

      setDeployerAddress(deployer);
      setIsPaused(paused);

      setLoadingStages(prev => ({ ...prev, bankState: false }));
    } catch (error) {
      console.error('[DICY] Failed to load bank basics:', error);
      if (error.message && error.message.includes('call revert exception')) {
        const expectedNetwork = getExpectedNetworkName();
        setErrorMessage(`Network mismatch: Please switch your wallet to the ${expectedNetwork.name} network (Chain ID: ${expectedNetwork.chainId})`);
      } else {
        setErrorMessage('Failed to load bank information: ' + error.message);
      }
    }
  }, [readOnlyContract]);

  const loadUserState = useCallback(async () => {
    if (!readOnlyContract || !userState.userAddress) return;
    try {
      console.log('[DICY] Loading user state...');

      // Execute user queries in parallel for better performance
      const [userInfo, userBalance] = await Promise.all([
        readOnlyContract.getInvestorInfo(userState.userAddress),
        readOnlyContract.balanceOf(userState.userAddress)
      ]);

      setUserState(prev => ({
        ...prev,
        userShares: userInfo.shares,
        userValue: userInfo.currentValue,
        isInvestor: userBalance > 0
      }));

      setLoadingStages(prev => ({ ...prev, userState: false }));
    } catch (error) {
      console.error('[DICY] Failed to load user state:', error);
      if (error.message && error.message.includes('call revert exception')) {
        const expectedNetwork = getExpectedNetworkName();
        setErrorMessage(`Network mismatch: Please switch your wallet to the ${expectedNetwork.name} network (Chain ID: ${expectedNetwork.chainId})`);
      }
    }
  }, [readOnlyContract, userState.userAddress]);

  const loadRecentBets = useCallback(async () => {
    if (!readOnlyContract) return;
    try {
      console.log('[DICY] Loading recent bets...');

      // Get recent bets
      const bets = await readOnlyContract.getRecentBets(10);

      // Enhance bets with transaction information if we have an ethers contract
      if (ethersContract && bets.length > 0) {
        const cacheKey = `dicy_recentBets_${bets.length}`;

        const enhancedBets = await deduplicateRequest(cacheKey, async () => {
          console.log(`[DICY] Loading recent bet transaction info`);

          const betPlacedEvents = await ethersContract.queryFilter(
            ethersContract.filters.BetPlaced(),
            0,
            'latest'
          );

          // Map bets to their transaction information
          return bets.map(bet => {
            const matchingEvent = betPlacedEvents.find(event =>
              event.args &&
              event.args.betId &&
              bet.id &&
              event.args.betId.toString() === bet.id.toString()
            );
            return {
              ...bet,
              transactionHash: matchingEvent ? matchingEvent.transactionHash : null
            };
          });
        });

        setRecentBets(enhancedBets);
      } else {
        setRecentBets(bets);
      }

      setLoadingStages(prev => ({ ...prev, recentBets: false }));
    } catch (error) {
      console.error('[DICY] Failed to load recent bets:', error);
      if (error.message && error.message.includes('call revert exception')) {
        const expectedNetwork = getExpectedNetworkName();
        setErrorMessage(`Network mismatch: Please switch your wallet to the ${expectedNetwork.name} network (Chain ID: ${expectedNetwork.chainId})`);
      }
    }
  }, [readOnlyContract, ethersContract]);

  const loadTransactionHistory = useCallback(async () => {
    if (transactionsCollapsed) return; // Skip if panel is collapsed

    if (!ethersContract || !userState.userAddress) {
      setLoadingStages(prev => ({ ...prev, transactions: false }));
      return;
    }

    try {
      console.log('[DICY] Loading transaction history...');
      await loadTransactionInfo();
      setLoadingStages(prev => ({ ...prev, transactions: false }));
    } catch (error) {
      console.error('[DICY] Failed to load transaction history:', error);
    }
  }, [ethersContract, userState.userAddress, transactionsCollapsed]);

  const initializeWalletFeatures = useCallback(async () => {
    if (!isWalletInitialized) return;
    try {
      console.log('[DICY] Initializing wallet features...');

      // Set contracts for user interactions
      if (signerContract) {
        setDicyContract(signerContract);
      } else {
        setDicyContract(null);
      }

      if (provider) {
        const ethersContract = new ethers.Contract(contractAddresses.DICY, DICY.abi, provider);
        setEthersContract(ethersContract);
      } else {
        setEthersContract(null);
      }

      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
    } catch (error) {
      console.error('[DICY] Failed to initialize wallet features:', error);
    }
  }, [isWalletInitialized, signerContract, userState.userAddress]);

  // Load transaction information
  const loadTransactionInfo = useCallback(async () => {
    if (!ethersContract || !userState.userAddress) return;

    const cacheKey = `dicy_userTransactions_${userState.userAddress}`;

    return deduplicateRequest(cacheKey, async () => {
      try {
        console.log(`[DICY] Loading user transaction history`);

        // Get user-related transaction events

        // Batch event queries for optimal RPC efficiency (6 calls -> 3 calls)
        const [
          [betPlacedEvents, betResolvedEvents], // Bet events batch
          [depositEvents, withdrawEvents],      // Investment events batch
          [transferFromEvents, transferToEvents] // Transfer events batch
        ] = await Promise.all([
          // Batch 1: Bet-related events
          Promise.all([
            ethersContract.queryFilter(
              ethersContract.filters.BetPlaced(null, userState.userAddress),
              0,
              'latest'
            ),
            ethersContract.queryFilter(
              ethersContract.filters.BetResolved(null, userState.userAddress),
              0,
              'latest'
            )
          ]),
          // Batch 2: Investment events
          Promise.all([
            ethersContract.queryFilter(
              ethersContract.filters.InvestorDeposit(userState.userAddress),
              0,
              'latest'
            ),
            ethersContract.queryFilter(
              ethersContract.filters.InvestorWithdraw(userState.userAddress),
              0,
              'latest'
            )
          ]),
          // Batch 3: Transfer events using advanced topic filtering
          Promise.all([
            // Transfers FROM user (using topic filter for better efficiency)
            ethersContract.queryFilter({
              address: ethersContract.address,
              topics: [
                ethers.utils.id("Transfer(address,address,uint256)"),
                ethers.utils.hexZeroPad(userState.userAddress, 32), // from user
                null // to any
              ],
              fromBlock: 0,
              toBlock: 'latest'
            }),
            // Transfers TO user (using topic filter for better efficiency)
            ethersContract.queryFilter({
              address: ethersContract.address,
              topics: [
                ethers.utils.id("Transfer(address,address,uint256)"),
                null, // from any
                ethers.utils.hexZeroPad(userState.userAddress, 32) // to user
              ],
              fromBlock: 0,
              toBlock: 'latest'
            })
          ])
        ]);

        // Combine and deduplicate transfer events
        const allTransferEvents = [...transferFromEvents, ...transferToEvents];
        const uniqueTransferEvents = allTransferEvents.filter((event, index, arr) =>
          arr.findIndex(e => e.transactionHash === event.transactionHash) === index
        );

        // Sort events by block number (most recent first)
        const sortedBetTxs = [...betPlacedEvents, ...betResolvedEvents]
          .sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber))
          .slice(0, 10); // Last 10 bet-related transactions

        const sortedInvestTxs = depositEvents
          .sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber))
          .slice(0, 10); // Last 10 deposits

        const sortedWithdrawTxs = withdrawEvents
          .sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber))
          .slice(0, 10); // Last 10 withdrawals

        const sortedTransferTxs = uniqueTransferEvents
          .filter(event =>
            // Filter out minting/burning transactions (to/from zero address)
            event.args.from !== ethers.constants.AddressZero &&
            event.args.to !== ethers.constants.AddressZero
          )
          .sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber))
          .slice(0, 10); // Last 10 transfers

        setTransactions({
          recentBetTxs: sortedBetTxs,
          recentInvestTxs: sortedInvestTxs,
          recentWithdrawTxs: sortedWithdrawTxs,
          recentTransferTxs: sortedTransferTxs
        });

      } catch (error) {
        console.error('Failed to load transaction info:', error);
        setTransactions({
          recentBetTxs: [],
          recentInvestTxs: [],
          recentWithdrawTxs: [],
          recentTransferTxs: []
        });
      }
    });
  }, [ethersContract, userState.userAddress]);

  // Load bank data function
  const loadBankData = useCallback(async (isInitialLoad = false) => {
    if (!dicyContract) return;
    try {
      
      // Get bank information in parallel for better performance
      const [bankValue, totalSupply] = await Promise.all([
        dicyContract.getBankValue(),
        dicyContract.totalSupply()
      ]);

      setBankState({
        totalValue: bankValue,
        maxBet: 0, // Will be calculated client-side when needed
        totalShares: totalSupply
      });

      // Get deployer address and pause status
      const deployer = await dicyContract.deployer();
      const paused = await dicyContract.paused();
      
      setDeployerAddress(deployer);
      setIsPaused(paused);

      // Get user information if connected
      if (userState.userAddress) {
        // Execute user queries in parallel for better performance
        const [userInfo, userBalance] = await Promise.all([
          dicyContract.getInvestorInfo(userState.userAddress),
          dicyContract.balanceOf(userState.userAddress)
        ]);
        
        setUserState(prev => ({
          ...prev,
          userShares: userInfo.shares,
          userValue: userInfo.currentValue,
          isInvestor: userBalance > 0
        }));
      }

      // Get recent bets with transaction hashes
      const bets = await dicyContract.getRecentBets(10);

      // Enhance bets with transaction information if we have an ethers contract
      if (ethersContract && bets.length > 0) {
        const betCacheKey = `dicy_recentBetsEnhancement_${bets.length}`;

        const enhancedBets = await deduplicateRequest(betCacheKey, async () => {
          try {
            console.log(`[DICY] Loading recent bet enhancement data`);

            // Get the range of bet IDs to query
            const betIds = bets.map(bet => bet.betId);
            const minBetId = Math.min(...betIds.map(id => Number(id)));
            const maxBetId = Math.max(...betIds.map(id => Number(id)));

            // Query BetPlaced and BetResolved events for these bet IDs (smart optimized range)

            // Batch bet events query for enhanced recent bets (2 calls -> 1 batch)
            const [betPlacedEvents, betResolvedEvents] = await Promise.all([
              ethersContract.queryFilter(
                ethersContract.filters.BetPlaced(),
                0,
                'latest'
              ),
              ethersContract.queryFilter(
                ethersContract.filters.BetResolved(),
                0,
                'latest'
              )
            ]);

            // Create maps for quick lookup
            const placedTxMap = {};
            const resolvedTxMap = {};

            betPlacedEvents.forEach(event => {
              const betId = Number(event.args.betId);
              placedTxMap[betId] = event.transactionHash;
            });

            betResolvedEvents.forEach(event => {
              const betId = Number(event.args.betId);
              resolvedTxMap[betId] = event.transactionHash;
            });

            // Enhance bet objects with transaction hashes
            return bets.map(bet => ({
              ...bet,
              placedTxHash: placedTxMap[Number(bet.betId)] || null,
              resolvedTxHash: resolvedTxMap[Number(bet.betId)] || null
            }));

          } catch (error) {
            console.warn('Failed to load transaction hashes for recent bets:', error);
            // Fallback to original bets without transaction hashes
            return bets;
          }
        });

        setRecentBets(enhancedBets);
      } else {
        setRecentBets(bets);
      }

    } catch (error) {
      console.error('Failed to load bank data:', error);
      setErrorMessage('Failed to load DICY data');
    }
  }, [dicyContract, userState.userAddress]);

  // Transaction data loaded lazily when panel is expanded (like TicTacToe)

  // Reset initialization only when user address actually changes (not just gets set)
  const prevUserAddressRef = useRef();
  useEffect(() => {
    if (prevUserAddressRef.current && prevUserAddressRef.current !== userState.userAddress) {
      // Only reset if address actually changed (not just initial load)
      setIsComponentInitialized(false);
    }
    prevUserAddressRef.current = userState.userAddress;
  }, [userState.userAddress]);

  // Simplified wallet initialization
  useEffect(() => {
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState(prev => ({ ...prev, userAddress: accounts[0] }));
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('DICY: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    initWallet();
  }, []);

  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `DICY`;

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Initialize contract explorer link
  useEffect(() => {
    const initExplorerLink = async () => {
      try {
        if (provider && contractAddresses.DICY) {
          const explorerSupported = await hasExplorerSupport(provider);
          if (explorerSupported) {
            const link = await getAddressLink(provider, contractAddresses.DICY);
            setContractExplorerLink(link);
          }
        }
      } catch (error) {
        console.error('Failed to initialize explorer link:', error);
      }
    };
    initExplorerLink();
  }, [isWalletInitialized]);

  // Progressive initialization with staggered loading (data only)
  useEffect(() => {
    // Prevent multiple initializations
    if (isComponentInitialized || !readOnlyContract) return;

    const initializeComponent = async () => {
      console.log('[DICY] Starting progressive initialization...');

      // Set UI ready immediately for non-blocking experience
      setUiReady(true);

      // Load bank basics first (highest priority)
      await loadBankBasics();

      // Load user state (medium priority)
      if (userState.userAddress) {
        await loadUserState();
      }

      // Load recent bets (medium-low priority)
      await loadRecentBets();

      // Mark component as initialized
      setIsComponentInitialized(true);

      // Transaction history loaded lazily when panel is expanded (like TicTacToe)
    };

    initializeComponent();
  }, [readOnlyContract, userState.userAddress, isComponentInitialized]);

  // Separate wallet features initialization (runs when wallet state changes)
  useEffect(() => {
    if (isWalletInitialized && signerContract) {
      initializeWalletFeatures();
    }
  }, [isWalletInitialized, signerContract, initializeWalletFeatures]);

  // Auto-adjust bet amount when number changes and bet exceeds new max
  useEffect(() => {
    try {
      if (betAmount && number && bankState.totalValue) {
        const maxBetStr = getMaxBetForNumber(number);
        const maxBet = parseFloat(maxBetStr);
        const currentBet = parseFloat(betAmount);
        
        // Only adjust if current bet exceeds max bet and values are valid
        // Skip adjustment if max bet is unlimited
        if (isFinite(currentBet) && isFinite(maxBet) && 
            currentBet > maxBet && maxBet > 0 && 
            maxBetStr !== '0' && maxBetStr !== 'unlimited') {
          // Cap the adjustment to prevent very large numbers
          const adjustedAmount = Math.min(maxBet, 1000000); // Cap at 1M POL
          setBetAmount(adjustedAmount.toString());
        }
      }
    } catch (error) {
      console.warn('Error in auto-adjust bet amount:', error);
    }
  }, [number, bankState.totalValue]);

  // Load transaction history when panel is expanded (like TicTacToe)
  useEffect(() => {
    if (!transactionsCollapsed && userState.userAddress && loadingStages.transactions) {
      loadTransactionHistory();
    }
  }, [transactionsCollapsed, userState.userAddress, loadingStages.transactions, loadTransactionHistory]);

  // Event handler for updates
  const handleDicyUpdate = useCallback(async (eventName) => {
    await loadBankData(false); // Event-driven update - no loading state change
    // Transaction history refreshed lazily when panel is expanded (like TicTacToe)
  }, [loadBankData]);

  // Enhanced event handlers for bet tracking
  // BetPlaced(uint256 indexed betId, address indexed player, uint256 amount, uint8 number)
  const handleBetPlaced = useCallback(async (betId, player, amount, number) => {
    // Check if this bet is from the current user
    if (player && userState.userAddress &&
        player.toLowerCase() === userState.userAddress.toLowerCase()) {
      // Keep the bet pending until resolved (status already set in handlePlaceBet)
    }
    await handleDicyUpdate('BetPlaced');
  }, [handleDicyUpdate, userState.userAddress]);

  // BetResolved(uint256 indexed betId, address indexed player, uint8 outcome, bool won, uint256 payout, uint256 bankAfter)
  const handleBetResolved = useCallback(async (betId, player, outcome, won, payout, bankAfter) => {
    // Check if this bet resolution is from the current user
    if (player && userState.userAddress &&
        player.toLowerCase() === userState.userAddress.toLowerCase()) {

      try {
        // Get the full bet details from the contract using betId
        const contractForQuery = readOnlyContract || await createContract(contractAddresses.DICY, DICY.abi, false);
        if (!contractForQuery) {
          console.warn('[DICY] No contract available for bet details query');
          return;
        }
        const betDetails = await contractForQuery.bets(betId);
        const betAmount = betDetails.amount;
        const chosenNumber = betDetails.number;

        // Calculate win amount (payout minus original bet)
        const winAmount = won ? ethersUtils.toBN(payout).sub(ethersUtils.toBN(betAmount)) : ethersUtils.toBN('0');

        setLastBetResult({
          outcome: outcome.toString(),
          chosenNumber: chosenNumber.toString(),
          betAmount: betAmount,
          winAmount: winAmount,
          won: won
        });

        setBetStatus(won ? 'won' : 'lost');

        // Reset to idle after 10 seconds to allow new bets
        setTimeout(() => {
          setBetStatus('idle');
          setLastBetResult(null);
        }, 10000);
      } catch (error) {
        console.error('Error fetching bet details:', error);
        setBetStatus('idle'); // Reset on error
      }
    }
    await handleDicyUpdate('BetResolved');
  }, [handleDicyUpdate, userState.userAddress, readOnlyContract]);

  // Enhanced event handlers for investment tracking
  // InvestorDeposit(address indexed investor, uint256 amount, uint256 newShares, uint256 totalBankAfter)
  const handleInvestorDeposit = useCallback(async (investor, amount, newShares, totalBankAfter) => {
    // Check if this deposit is from the current user
    if (investor && userState.userAddress &&
        investor.toLowerCase() === userState.userAddress.toLowerCase()) {

      setLastInvestResult({
        amount: amount,
        newShares: newShares,
        totalBankAfter: totalBankAfter
      });

      setInvestStatus('success');

      // Reset to idle after 8 seconds
      setTimeout(() => {
        setInvestStatus('idle');
        setLastInvestResult(null);
      }, 8000);
    }
    await handleDicyUpdate('InvestorDeposit');
  }, [handleDicyUpdate, userState.userAddress]);

  // InvestorWithdraw(address indexed investor, uint256 amount, uint256 sharesRedeemed, uint256 totalBankAfter)
  const handleInvestorWithdraw = useCallback(async (investor, amount, sharesRedeemed, totalBankAfter) => {
    // Check if this withdrawal is from the current user
    if (investor && userState.userAddress &&
        investor.toLowerCase() === userState.userAddress.toLowerCase()) {

      setLastWithdrawResult({
        amount: amount,
        sharesRedeemed: sharesRedeemed,
        totalBankAfter: totalBankAfter
      });

      setWithdrawStatus('success');

      // Reset to idle after 8 seconds
      setTimeout(() => {
        setWithdrawStatus('idle');
        setLastWithdrawResult(null);
      }, 8000);
    }
    await handleDicyUpdate('InvestorWithdraw');
  }, [handleDicyUpdate, userState.userAddress]);

  // Set up blockchain event listening
  const eventState = useBlockchainEvents({
    contract: ethersContract,
    gameId: null, // No specific game ID for DICY
    events: [
      {
        name: 'InvestorDeposit',
        filter: [], // Listen to all deposit events
        handler: handleInvestorDeposit
      },
      {
        name: 'InvestorWithdraw',
        filter: [], // Listen to all withdraw events
        handler: handleInvestorWithdraw
      },
      {
        name: 'BetPlaced',
        filter: [], // Listen to all bet events
        handler: handleBetPlaced
      },
      {
        name: 'BetResolved',
        filter: [], // Listen to all bet resolution events
        handler: handleBetResolved
      },
      {
        name: 'Transfer',
        filter: [], // Listen to all token transfer events
        handler: () => handleDicyUpdate('Transfer')
      }
    ],
    enabled: !loadingStages.userFeatures && ethersContract !== null,
    fallbackInterval: 15000 // 15 second fallback polling (matches other games)
  });

  // Deposit to bank
  const handleDeposit = async () => {
    if (!depositAmount) return;

    try {
      // Reset previous result and set status to pending
      setLastInvestResult(null);
      setInvestStatus('pending');
      setPendingTx(true);

      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.depositToBank({
        value: ethersUtils.toWei(depositAmount, 'ether')
      });
      await tx.wait();

      setDepositAmount('');
      setPendingTx(false);

      // Note: investStatus will be updated by the event handler (handleInvestorDeposit)
    } catch (error) {
      console.error('Deposit failed:', error);
      setErrorMessage('Deposit failed: ' + error.message);
      setPendingTx(false);
      setInvestStatus('idle'); // Reset status on error
    }
  };

  // Withdraw from bank
  const handleWithdraw = async () => {
    if (!withdrawShares) return;

    try {
      // Reset previous result and set status to pending
      setLastWithdrawResult(null);
      setWithdrawStatus('pending');
      setPendingTx(true);

      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.withdrawFromBank(withdrawShares);
      await tx.wait();

      setWithdrawShares('');
      setPendingTx(false);

      // Note: withdrawStatus will be updated by the event handler (handleInvestorWithdraw)
    } catch (error) {
      console.error('Withdraw failed:', error);
      setErrorMessage('Withdraw failed: ' + error.message);
      setPendingTx(false);
      setWithdrawStatus('idle'); // Reset status on error
    }
  };

  // Place bet
  const handlePlaceBet = async () => {
    if (!betAmount || !number) return;

    try {
      // Reset previous bet result and set status to pending
      setLastBetResult(null);
      setBetStatus('pending');
      setPendingTx(true);

      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.placeBet(number, {
        value: ethersUtils.toWei(betAmount, 'ether')
      });
      await tx.wait();

      setBetAmount('');
      setPendingTx(false);

      // Note: betStatus will be updated by the event handlers (handleBetPlaced/handleBetResolved)
    } catch (error) {
      console.error('Bet failed:', error);
      setErrorMessage('Bet failed: ' + error.message);
      setPendingTx(false);
      setBetStatus('idle'); // Reset status on error
    }
  };

  // Transfer tokens
  const handleTransfer = async () => {
    if (!transferTo || !transferAmount) return;
    
    try {
      setPendingTx(true);
      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.transfer(transferTo, transferAmount);
      await tx.wait();
      setTransferTo('');
      setTransferAmount('');
      setPendingTx(false);
    } catch (error) {
      console.error('Transfer failed:', error);
      setErrorMessage('Transfer failed: ' + error.message);
      setPendingTx(false);
    }
  };

  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For DICY page, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = () => {
    // For DICY page, we'll navigate back to main page after disconnect
    window.location.href = '/';
  };

  // Get expected network name based on current environment
  const getExpectedNetworkName = () => {
    const hostname = window.location.hostname;

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return { name: 'Local Hardhat', chainId: 31337 };
    } else if (hostname.includes('devcli')) {
      return { name: 'DevCLI', chainId: 31338 };
    } else if (hostname.includes('test') || hostname.includes('amoy')) {
      return { name: 'Polygon Amoy', chainId: 80002 };
    } else if (hostname.includes('preprod')) {
      return { name: 'Polygon Mainnet', chainId: 137 };
    } else {
      // Default to Polygon mainnet for production
      return { name: 'Polygon Mainnet', chainId: 137 };
    }
  };

  const formatEth = (wei) => {
    const ethValue = ethersUtils.fromWei(wei.toString(), 'ether');
    return formatWithSeparators(ethValue);
  };

  // Helper function to format numbers with Swiss thousand separators
  const formatWithSeparators = (value) => {
    if (!value || value === '0' || value === 'unlimited') return value;
    try {
      // Handle both string and numeric values
      const numStr = typeof value === 'string' ? value : value.toString();
      // Split into integer and decimal parts
      const parts = numStr.split('.');
      // Add Swiss separator (') to integer part
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return parts.join('.');
    } catch (error) {
      return value;
    }
  };

  // Format number with at most 5 significant digits for cleaner display
  const formatSignificant = (value, maxSignificantDigits = 5) => {
    const num = parseFloat(value);
    if (!isFinite(num) || num === 0) return '0';
    
    // For very small numbers (< 0.00001), show in scientific notation
    if (Math.abs(num) < 0.00001) {
      return num.toExponential(maxSignificantDigits - 1);
    }
    
    // For numbers >= 1, limit to maxSignificantDigits total digits
    if (Math.abs(num) >= 1) {
      const precision = Math.max(0, maxSignificantDigits - Math.floor(Math.log10(Math.abs(num))) - 1);
      return num.toFixed(precision);
    }
    
    // For numbers between 0 and 1, count significant digits after decimal
    const str = num.toString();
    const scientificMatch = str.match(/^(\d+\.?\d*)e-(\d+)$/);
    if (scientificMatch) {
      const [, mantissa, exponent] = scientificMatch;
      const exp = parseInt(exponent);
      const precision = Math.max(0, exp + maxSignificantDigits - 1);
      return num.toFixed(precision);
    }
    
    // Regular decimal number
    const precision = Math.max(0, maxSignificantDigits - 1 + Math.floor(-Math.log10(Math.abs(num))));
    return num.toFixed(precision);
  };

  // Format ETH with limited significant digits for display
  const formatEthSignificant = (wei, maxSignificantDigits = 5) => {
    const ethValue = ethersUtils.fromWei(wei.toString(), 'ether');
    const formatted = formatSignificant(ethValue, maxSignificantDigits);
    return formatWithSeparators(formatted);
  };

  const formatShares = (userShares, totalShares) => {
    if (!totalShares || totalShares === 0) return '0.00';
    return ((userShares / totalShares) * 100).toFixed(2); // Calculate actual percentage of total supply
  };

  // Calculate payout multiplier based on number (matches contract basis points arithmetic)
  const getPayoutMultiplier = (number) => {
    if (!number || number < 1 || number > 100) return 1;
    return 100 / number; // Return true fractional multiplier to match contract behavior
  };

  // Format multiplier with true fractional values
  const formatMultiplier = (number) => {
    if (!number || number < 1 || number > 100) return '1.0';
    const trueMultiplier = 100 / number; // True fractional value
    if (trueMultiplier < 3) {
      return trueMultiplier.toFixed(2); // 2 decimal places for < 3
    } else {
      return trueMultiplier.toFixed(1); // 1 decimal place for >= 3
    }
  };

  // Calculate max bet for current number
  const getMaxBetForNumber = (number) => {
    try {
      if (!bankState.totalValue || !number) return '0';
      
      // Ensure number is valid and avoid division by zero
      const safeNumber = Math.max(1, Math.min(100, parseInt(number) || 50));
      
      // Convert BigNumber to JavaScript number for calculation
      const bankValueWei = ethersUtils.toBN(bankState.totalValue);
      
      // Special case: number = 100 gives 1x multiplier (no profit above bet)
      // Since bank only risks the bet amount itself (no multiplier), no limit needed
      if (safeNumber >= 100) {
        return "unlimited";
      }
      
      // Safety check: prevent division by zero
      const denominator = 100 - safeNumber;
      if (denominator <= 0) {
        return "unlimited";
      }
      
      // 10% * equity * number/(100-number)
      // Use BigNumber arithmetic to match Solidity integer division exactly
      const maxBetWei = bankValueWei.div(10).mul(safeNumber).div(denominator);
      
      return formatEthSignificant(maxBetWei.toString());
    } catch (error) {
      console.warn('Error calculating max bet:', error);
      return '0';
    }
  };

  // Calculate win probability percentage
  const getWinProbability = (number) => {
    if (!number || number < 1 || number > 100) return 0;
    return (number / 101 * 100).toFixed(1); // number/101 as percentage
  };

  const getOutcomeText = (outcome, number) => {
    if (outcome <= number) return `Player Win (${outcome})`; 
    return `Player Loss (${outcome})`;
  };

  const getOutcomeColor = (outcome, won) => {
    if (outcome === 101) return 'text-green-600 bg-green-100';
    if (won) return 'text-blue-600 bg-blue-100';
    return 'text-red-600 bg-red-100';
  };

  // Show skeleton UI immediately while data loads
  if (!uiReady) {
    return <FullGameSkeleton />;
  }

  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  // Show error as banner, not blocking the entire page

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <Link
          to="/"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
        >
          ← Back to Games
        </Link>

        <TopRightBar
          onSwitchWallet={handleSwitchWallet}
          onWalletDisconnect={handleWalletDisconnect}
          showInTestMode={true}
        />
      </div>

      <div className="max-w-6xl mx-auto">

        {/* Error Banner - non-blocking */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Warning: {errorMessage}
            <button 
              onClick={() => setErrorMessage('')}
              className="ml-4 bg-red-600 text-white px-3 py-1 rounded text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Pause Status Banner */}
        {isPaused && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            ⚠️ Contract is paused. New deposits and bets are disabled. Withdrawals remain available.
          </div>
        )}

        <h1 className="text-4xl font-bold text-center mb-4 text-gray-800">
          DICY
        </h1>

        {/* Contract Address Link */}
        {contractExplorerLink && (
          <div className="text-center mb-4">
            <a
              href={contractExplorerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              📄 View Contract on Polygonscan
            </a>
          </div>
        )}

        <p className="text-center text-gray-600 italic mb-8">
          This is a homage to the 2016 Etheroll project and DICE token. <Link to="/blog/dicy-smart-contract-mechanics" className="text-blue-600 hover:text-blue-800 underline">Read more</Link>
        </p>

        {/* Game Rules - Collapsible */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button 
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer"
          >
            <span>How DICY Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-4'
          }`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-600">For Investors</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Deposit POL to become part of the house bank</li>
                  <li>Receive transferable ERC20 tokens representing ownership shares</li>
                  <li>Earn money when players lose, lose money when players win</li>
                  <li>Withdraw your share anytime or transfer tokens to others</li>
                  <li>Trade tokens freely - recipients can withdraw proportional bank value</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-red-600">For Players</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Choose your number (1 to 100)</li>
                  <li>Random outcome: 1-101 (101 is always house win)</li>
                  <li>Win if outcome ≤ your chosen number</li>
                  <li>Payout multiplier: 100 ÷ your number</li>
                  <li>Max bet: 10% × equity × number/(100-number)</li>
                </ul>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-center">
                <Link 
                  to="/blog/dicy-smart-contract-mechanics"
                  className="inline-flex items-center px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold rounded-lg transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Technical Details: Smart Contract Mechanics
                </Link>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                Learn about the ERC20 token mechanics, randomness generation, and mathematical precision
              </p>
            </div>
          </div>
        </div>

        {/* Deployer Controls */}
        {userState.userAddress && deployerAddress &&
         userState.userAddress.toLowerCase() === deployerAddress.toLowerCase() && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-2xl font-semibold mb-4 text-gray-700">Contract Administration</h3>
            <div className="flex items-center justify-center">
              <Link
                to="/asymmetric/DICY/admin"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Go to Admin Panel
              </Link>
            </div>
            <p className="text-sm text-gray-600 text-center mt-3">
              Access advanced contract controls, pause functionality, and emergency killswitch
            </p>
          </div>
        )}

        {/* Betting Interface */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Place Your Bet</h2>
          <form onSubmit={(e) => { e.preventDefault(); handlePlaceBet(); }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Number Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number {number} and below (out of 101)
                </label>
                <div className="text-sm text-gray-600 mb-2">
                  {getWinProbability(number)}% chance to get {formatMultiplier(number)}x of your bet, {(100 - parseFloat(getWinProbability(number))).toFixed(1)}% chance to lose
                </div>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={number}
                  onChange={(e) => setNumber(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  disabled={pendingTx}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 ({formatMultiplier(1)}x payout)</span>
                  <span>50 ({formatMultiplier(50)}x payout)</span>
                  <span>100 ({formatMultiplier(100)}x payout)</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Probability: Number/101 chance to win
                </div>
                <div className="text-sm text-gray-600">
                  Payout: 100/Number
                </div>
              </div>
              
              {/* Bet Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bet Amount (POL)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handlePlaceBet();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={(() => {
                    const maxBet = getMaxBetForNumber(number);
                    return maxBet === 'unlimited' ? 'no limit. you cannot win this.' : `Max: ${maxBet}`;
                  })()}
                  disabled={pendingTx}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Potential payout: {betAmount ? formatWithSeparators((parseFloat(betAmount) * getPayoutMultiplier(number)).toFixed(4)) : '0'} POL
                </div>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={pendingTx || !betAmount || parseFloat(betAmount) <= 0}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-md transition-colors"
            >
              {pendingTx ? 'Processing...' : 'Place Bet'}
            </button>


            {/* Bet Status Message */}
            <div className="mt-3 text-center">
              {betStatus === 'idle' && (
                <div className="text-gray-600 text-sm">
                  Place your bet
                </div>
              )}
              {betStatus === 'pending' && (
                <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-md animate-pulse border border-blue-200">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                    <span>Transaction pending</span>
                  </div>
                </div>
              )}
              {betStatus === 'lost' && lastBetResult && (
                <div className="bg-red-100 text-red-800 px-4 py-2 rounded-md border border-red-200">
                  You lost {formatEth(lastBetResult.betAmount)} POL. Outcome {lastBetResult.outcome}, you chose {lastBetResult.chosenNumber}.
                </div>
              )}
              {betStatus === 'won' && lastBetResult && (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-md border border-green-200">
                  You won {formatEth(lastBetResult.winAmount)} POL. Outcome {lastBetResult.outcome}, you chose {lastBetResult.chosenNumber}.
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Investor Interface */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button 
            onClick={() => setInvestorsCollapsed(!investorsCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer mb-4"
          >
            <span>Investor Dashboard</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${investorsCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            investorsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
          }`}>
            {/* Investment Overview */}
            <div className="bg-blue-50 p-4 rounded mb-4">
              <h3 className="font-semibold text-blue-800 mb-2">Investment Overview</h3>
              
              {/* Row 1: Totals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Bank</p>
                  <p className="text-lg font-semibold">{formatEth(bankState.totalValue)} POL</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Share</p>
                  <p className="text-lg font-semibold">100%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Tokens</p>
                  <p className="text-lg font-semibold">{formatWithSeparators(bankState.totalShares.toString())}</p>
                </div>
              </div>
              
              {/* Row 2: User Specific */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Your Bank Share</p>
                  <p className="text-lg font-semibold">{formatEth(userState.userValue)} POL</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Your Ownership</p>
                  <p className="text-lg font-semibold text-blue-800">{formatShares(userState.userShares, bankState.totalShares)}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Your Tokens</p>
                  <p className="text-lg font-semibold">{formatWithSeparators(userState.userShares.toString())}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Deposit */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-700">Invest in Bank</h3>
                <form onSubmit={(e) => { e.preventDefault(); handleDeposit(); }}>
                  <input
                    type="number"
                    step="0.01"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleDeposit();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
                    placeholder="Amount in POL"
                    disabled={pendingTx}
                  />
                  <button
                    type="submit"
                    disabled={pendingTx || !depositAmount || parseFloat(depositAmount) <= 0}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    {pendingTx ? 'Processing...' : 'Deposit to Bank'}
                  </button>

                  {/* Deposit Status Message */}
                  <div className="mt-3 text-center">
                    {investStatus === 'pending' && (
                      <div className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md animate-pulse border border-blue-200 text-sm">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          <span>Investment pending</span>
                        </div>
                      </div>
                    )}
                    {investStatus === 'success' && lastInvestResult && (
                      <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md border border-green-200 text-sm">
                        Successfully invested {formatEth(lastInvestResult.amount)} POL. Received {formatWithSeparators(lastInvestResult.newShares.toString())} shares.
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {/* Withdraw */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-700">Withdraw from Bank</h3>
                <form onSubmit={(e) => { e.preventDefault(); handleWithdraw(); }}>
                  <input
                    type="number"
                    value={withdrawShares}
                    onChange={(e) => setWithdrawShares(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleWithdraw();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
                    placeholder="Tokens to redeem"
                    disabled={pendingTx || !userState.isInvestor}
                  />
                  <button
                    type="submit"
                    disabled={pendingTx || !withdrawShares || !userState.isInvestor || parseInt(withdrawShares) <= 0}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    {pendingTx ? 'Processing...' : 'Withdraw Tokens'}
                  </button>

                  {/* Withdrawal Status Message */}
                  <div className="mt-3 text-center">
                    {withdrawStatus === 'pending' && (
                      <div className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md animate-pulse border border-blue-200 text-sm">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          <span>Withdrawal pending</span>
                        </div>
                      </div>
                    )}
                    {withdrawStatus === 'success' && lastWithdrawResult && (
                      <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md border border-green-200 text-sm">
                        Successfully withdrew {formatEth(lastWithdrawResult.amount)} POL. Redeemed {formatWithSeparators(lastWithdrawResult.sharesRedeemed.toString())} shares.
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Token Transfer */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button 
            onClick={() => setTransfersCollapsed(!transfersCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer mb-4"
          >
            <span>Transfer Tokens</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${transfersCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            transfersCollapsed ? 'max-h-0 opacity-0' : 'max-h-[400px] opacity-100'
          }`}>
            <div className="bg-blue-50 p-4 rounded mb-4">
              <h3 className="font-semibold text-blue-800 mb-2">Transfer Shares</h3>
              <p className="text-sm text-blue-600">
                Send your ownership tokens to another address. Recipients can then withdraw their proportional share of the bank or transfer to others.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Address
                </label>
                <input
                  type="text"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      document.querySelector('input[placeholder*="Max:"]').focus();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                  placeholder="0x..."
                  disabled={pendingTx || !userState.isInvestor}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Token Amount (Share Units)
                </label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTransfer();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                  placeholder={`Max: ${userState.userShares}`}
                  disabled={pendingTx || !userState.isInvestor}
                />
              </div>

              <button
                type="button"
                onClick={handleTransfer}
                disabled={pendingTx || !transferTo || !transferAmount || !userState.isInvestor || parseInt(transferAmount) <= 0 || parseInt(transferAmount) > userState.userShares}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded transition-colors"
              >
                {pendingTx ? 'Processing...' : 'Transfer Tokens'}
              </button>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => setTransactionsCollapsed(!transactionsCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer mb-4"
          >
            <span>Transaction History</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${transactionsCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            transactionsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[800px] opacity-100'
          }`}>
            {!userState.userAddress ? (
              <p className="text-gray-500 text-center py-4">Connect your wallet to view transaction history</p>
            ) : loadingStages.transactions ? (
              <TransactionPanelSkeleton />
            ) : (
              <div className="space-y-6">
                {/* Betting Transactions */}
                {transactions.recentBetTxs.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-gray-700">Betting Transactions</h4>
                    <div className="space-y-2">
                      {transactions.recentBetTxs.map((event, index) => (
                        <div key={`${event.transactionHash}-${index}`} className="bg-blue-50 p-3 rounded border">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              {event.event === 'BetPlaced' ? 'Bet Placed' : 'Bet Resolved'}
                              {event.event === 'BetPlaced' && ` - Number ${event.args.number}, Amount ${formatEth(event.args.amount)} POL`}
                              {event.event === 'BetResolved' && ` - Outcome ${event.args.outcome}, ${event.args.won ? 'Won' : 'Lost'}`}
                            </span>
                            <span className="text-xs text-gray-500">Block #{event.blockNumber}</span>
                          </div>
                          <div className="text-gray-600">
                            <TransactionLink value={event.transactionHash} provider={provider} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Investment Transactions */}
                {transactions.recentInvestTxs.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-gray-700">Investment Transactions</h4>
                    <div className="space-y-2">
                      {transactions.recentInvestTxs.map((event, index) => (
                        <div key={`${event.transactionHash}-${index}`} className="bg-green-50 p-3 rounded border">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              Bank Deposit - {formatEth(event.args.amount)} POL, {formatWithSeparators(event.args.newShares.toString())} shares
                            </span>
                            <span className="text-xs text-gray-500">Block #{event.blockNumber}</span>
                          </div>
                          <div className="text-gray-600">
                            <TransactionLink value={event.transactionHash} provider={provider} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Withdrawal Transactions */}
                {transactions.recentWithdrawTxs.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-gray-700">Withdrawal Transactions</h4>
                    <div className="space-y-2">
                      {transactions.recentWithdrawTxs.map((event, index) => (
                        <div key={`${event.transactionHash}-${index}`} className="bg-red-50 p-3 rounded border">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              Bank Withdrawal - {formatEth(event.args.amount)} POL, {formatWithSeparators(event.args.sharesRedeemed.toString())} shares
                            </span>
                            <span className="text-xs text-gray-500">Block #{event.blockNumber}</span>
                          </div>
                          <div className="text-gray-600">
                            <TransactionLink value={event.transactionHash} provider={provider} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Token Transfer Transactions */}
                {transactions.recentTransferTxs.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-gray-700">Token Transfer Transactions</h4>
                    <div className="space-y-2">
                      {transactions.recentTransferTxs.map((event, index) => {
                        const isUserSender = event.args.from.toLowerCase() === userState.userAddress.toLowerCase();
                        return (
                          <div key={`${event.transactionHash}-${index}`} className="bg-purple-50 p-3 rounded border">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-700">
                                {isUserSender ? 'Sent' : 'Received'} {formatWithSeparators(event.args.value.toString())} shares
                                {isUserSender ? ' to ' : ' from '}
                                <AddressDisplayLong address={isUserSender ? event.args.to : event.args.from} useExplorerLink={true} />
                              </span>
                              <span className="text-xs text-gray-500">Block #{event.blockNumber}</span>
                            </div>
                            <div className="text-gray-600">
                              <TransactionLink value={event.transactionHash} provider={provider} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* No transactions message */}
                {transactions.recentBetTxs.length === 0 &&
                 transactions.recentInvestTxs.length === 0 &&
                 transactions.recentWithdrawTxs.length === 0 &&
                 transactions.recentTransferTxs.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No recent transactions found</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Bets */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <button
            onClick={() => setBetsCollapsed(!betsCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer mb-4"
          >
            <span>Recent Bets</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${betsCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            betsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[600px] opacity-100'
          }`}>
            {recentBets.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No bets placed yet</p>
            ) : (
              <div className="space-y-3">
                {recentBets.map((bet) => (
                  <div key={bet.betId.toString()} className="border border-gray-200 rounded p-3">
                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-2 items-start">
                      <div>
                        <p className="text-sm text-gray-600">Player</p>
                        <p className="font-mono text-xs"><AddressDisplayLong address={bet.player} useExplorerLink={true} /></p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Bet</p>
                        <p className="font-semibold">{formatEth(bet.amount)} POL @ {bet.number}</p>
                        <p className="text-xs text-gray-500">Multiplier: {(100 / bet.number).toFixed(2)}x</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Outcome</p>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${getOutcomeColor(bet.outcome, bet.won)}`}>
                          {getOutcomeText(bet.outcome, bet.number)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Result</p>
                        <span className={`font-semibold ${bet.won ? 'text-green-600' : 'text-red-600'}`}>
                          {bet.won
                            ? `Won ${formatEth(ethersUtils.toBN(bet.payout).sub(ethersUtils.toBN(bet.amount)))} POL`
                            : `Lost ${formatEth(bet.amount)} POL`
                          }
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Time</p>
                        <p className="text-xs">{new Date(bet.timestamp * 1000).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Transaction</p>
                        <div className="text-xs">
                          {(bet.placedTxHash || bet.resolvedTxHash) ? (
                            <div className="text-gray-600">
                              <TransactionLink value={bet.placedTxHash || bet.resolvedTxHash} provider={provider} />
                            </div>
                          ) : (
                            <span className="text-gray-400">Loading...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DICYGame;